import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Loader2, AlertCircle, Inbox, Gavel, ShieldAlert,
  ArrowLeftRight,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import {
  getDashboardSummary, type DashboardSummary, type CaseBrief, type QueueItem,
} from '../api/dashboard';
import { ROLE_LABELS } from '../config/labels';
import StatusBadge from '../components/StatusBadge';
import TierBadge from '../components/TierBadge';

const STATUS_ORDER = [
  'DRAFT', 'SUBMITTED', 'IN_REVIEW', 'RETURNED', 'PENDING_DETERMINATION',
  'DETERMINED', 'APPROVED', 'ESCALATED', 'CLOSED',
];

function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="card p-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

function CaseRow({ item, showStep }: { item: CaseBrief | QueueItem; showStep?: boolean }) {
  const q = item as QueueItem;
  return (
    <Link
      to={`/cases/${item.id}`}
      className="flex items-center justify-between gap-2 p-3 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800 truncate">{item.case_number}</span>
          <StatusBadge status={item.status} size="sm" />
          {item.tier && <TierBadge tier={item.tier} size="sm" />}
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">
          {item.issuer_name || 'Unknown issuer'}
          {item.depositor_name ? ` - ${item.depositor_name}` : ''}
        </div>
        {showStep && q.step_name && (
          <div className="text-xs text-slate-500 mt-0.5">Step: {q.step_name} ({q.required_role})</div>
        )}
      </div>
    </Link>
  );
}

function SectionCard({ title, icon, children, empty }: {
  title: string; icon: typeof Inbox; children: React.ReactNode; empty?: string;
}) {
  const Icon = icon;
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-primary-600" />
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      {children}
      {!children && <p className="text-sm text-slate-400">{empty || 'Nothing here.'}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardSummary();
      setSummary(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }
  if (error || !summary) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="card p-6 flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error || 'No data'}</span>
        </div>
      </div>
    );
  }

  const roleLabel = ROLE_LABELS[user?.role || ''] || user?.role || '';
  const statusCounts = summary.my_cases_by_status || summary.all_cases_by_status || {};
  const totalCases = Object.values(statusCounts).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <LayoutDashboard className="text-primary-600" size={24} />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Welcome back, {user?.full_name} ({roleLabel})</p>
        </div>
      </div>

      {/* Tier distribution */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Tier distribution (visible cases)</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Standard" value={summary.tier_distribution.STANDARD || 0} />
          <StatCard label="Enhanced Review" value={summary.tier_distribution.ENHANCED_REVIEW || 0} />
          <StatCard label="Escalate" value={summary.tier_distribution.ESCALATE || 0} />
          <StatCard label="Unscored" value={summary.tier_distribution.NONE || 0} />
        </div>
      </div>

      {/* Analyst / reviewer: my cases by status */}
      {statusCounts && Object.keys(statusCounts).length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">
            {summary.my_cases_by_status ? 'My cases by status' : 'All cases by status'}
          </h2>
          <div className="card p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {STATUS_ORDER.filter((s) => statusCounts[s]).map((s) => (
                <div key={s} className="flex items-center justify-between p-2 rounded bg-slate-50">
                  <StatusBadge status={s} size="sm" />
                  <span className="text-sm font-semibold text-slate-700">{statusCounts[s]}</span>
                </div>
              ))}
              {totalCases === 0 && (
                <p className="text-sm text-slate-400 col-span-full">No cases.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Queues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {summary.my_returned_queue && (
          <SectionCard title="Returned for more docs (my queue)" icon={ArrowLeftRight}
            empty="No returned cases.">
            {summary.my_returned_queue.length > 0 && (
              <div className="space-y-2">
                {summary.my_returned_queue.map((c) => <CaseRow key={c.id} item={c} />)}
              </div>
            )}
          </SectionCard>
        )}
        {summary.my_review_queue && (
          <SectionCard title="My review queue" icon={Inbox} empty="No cases awaiting your review.">
            {summary.my_review_queue.length > 0 && (
              <div className="space-y-2">
                {summary.my_review_queue.map((c) => <CaseRow key={c.id} item={c} showStep />)}
              </div>
            )}
          </SectionCard>
        )}
        {summary.my_approval_queue && (
          <SectionCard title="My approval queue" icon={Gavel} empty="No cases awaiting your approval.">
            {summary.my_approval_queue.length > 0 && (
              <div className="space-y-2">
                {summary.my_approval_queue.map((c) => <CaseRow key={c.id} item={c} showStep />)}
              </div>
            )}
          </SectionCard>
        )}
        {summary.escalated && (
          <SectionCard title="Escalated cases" icon={ShieldAlert} empty="No escalated cases.">
            {summary.escalated.length > 0 && (
              <div className="space-y-2">
                {summary.escalated.map((c) => <CaseRow key={c.id} item={c} />)}
              </div>
            )}
          </SectionCard>
        )}
      </div>

    </div>
  );
}
